// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { useSpreedlyFrame } from "@insite/client-framework/Common/Hooks/useSpreedlyFrame";
import {
    IFrame,
    TokenEx as TokenExType,
    TokenExIframeStyles,
} from "@insite/client-framework/Common/Hooks/useTokenExFrame";
import { useTokenExFrame } from "@insite/client-framework/Common/Hooks/useTokenExFrame";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import validateCreditCard from "@insite/client-framework/Common/Utilities/validateCreditCard";
import logger from "@insite/client-framework/Logger";
import { TokenExConfig } from "@insite/client-framework/Services/SettingsService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadTokenExConfig from "@insite/client-framework/Store/Context/Handlers/LoadTokenExConfig";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import checkoutWithPayPal from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/CheckoutWithPayPal";
import placeOrder from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/PlaceOrder";
import preloadOrderConfirmationData from "@insite/client-framework/Store/Pages/OrderConfirmation/Handlers/PreloadOrderConfirmationData";
import translate from "@insite/client-framework/Translate";
import CreditCardBillingAddressEntry, {
    CreditCardBillingAddressEntryStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CreditCardBillingAddressEntry";
import PaymentProfileBillingAddress from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/PaymentProfileBillingAddress";
import SavedPaymentProfileEntry, {
    SavedPaymentProfileEntryStyles,
    SavedPaymentProfileValidation,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/SavedPaymentProfileEntry";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select/Select";
import CreditCardDetailsEntry, {
    CreditCardDetailsValidation,
    CreditCardStyles,
} from "@rugby/Overrides/Widgets/CheckoutReviewAndSubmit/CreditCardDetailsEntry";

import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import setCheckoutPaymentMethod from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetCheckoutPaymentMethod";
import { PaymentMethodDto } from "@insite/client-framework/Types/ApiModels";
import usePaymentDetailsCartState from "@insite/content-library/Hooks/Payments/usePaymentDetailsCartState";
import usePaymentDetailsFormHandlers from "@insite/content-library/Hooks/Payments/usePaymentDetailsFormHandlers";
import usePaymentDetailsFormState from "@insite/content-library/Hooks/Payments/usePaymentDetailsFormState";
import usePaymentDetailsFormValidation from "@insite/content-library/Hooks/Payments/usePaymentDetailsFormValidation";
import { HasToasterContext } from "@insite/mobius/Toast/ToasterContext";
import { generateTokenExFrameStyleConfig } from "@insite/mobius/TokenExFrame";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import HistoryContext, { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Ach from "@rugby/App/Components/Ach/Ach";
import { xAchState } from "@rugby/App/Store/Reducers";
import { xInvoicePaymentState } from "@rugby/App/Store/Reducers/InvoicePaymentReducer";
import { getPaymentMethod } from "@rugby/App/Store/Selectors/InvoicePaymentSelectors";
import { MediaQuery } from "@rugby/XDL/Utilities";
import React, { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css, ThemeContext } from "styled-components";

const mapStateToProps = (state: ApplicationState & xInvoicePaymentState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const customSettings: any = getSettingsCollection(state).customSettings;
    const achPaymentMethodName = customSettings["achPaymentMethodName"] ? customSettings["achPaymentMethodName"] : "";
    const currentPage = getCurrentPage(state).type;

    return {
        cartState,
        billToState: getBillToState(state, cartState.value ? cartState.value.billToId : undefined),
        cartSettings: getSettingsCollection(state).cartSettings,
        tokenExConfigs: state.context.tokenExConfigs,
        orderConfirmationPageLink: getPageLinkByPageType(state, "OrderConfirmationPage"),
        savedPaymentsPageLink: getPageLinkByPageType(state, "SavedPaymentsPage"),
        session: state.context.session,
        signInPageLink: getPageLinkByPageType(state, "SignInPage"),
        checkoutReviewAndSubmitPageLink: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage"),
        payPalRedirectUri: state.pages.checkoutReviewAndSubmit.payPalRedirectUri,
        location: getLocation(state),
        achPaymentMethodName,
        allowedPaymentMethodsForInvoicePayment: customSettings.allowedPaymentMethodsForInvoicePayment,
        isInvoicePaymentPage: currentPage === "InvoicePaymentPage",
    };
};

const mapDispatchToProps = {
    loadTokenExConfig,
    placeOrder,
    checkoutWithPayPal,
    preloadOrderConfirmationData,
    loadBillTo,
    loadCurrentCountries,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext & {
        customPaymentMethods: PaymentMethodDto[] | null;
        makePaymentSubmit: (data: any) => void;
    };

export interface CheckoutReviewAndSubmitPaymentDetailsStyles {
    form?: InjectableCss;
    fieldset?: InjectableCss;
    paymentDetailsHeading?: TypographyPresentationProps;
    paymentMethodPayPalText?: TypographyPresentationProps;
    paymentMethodAndPONumberContainer?: GridContainerProps;
    paymentMethodGridItem?: GridItemProps;
    paymentMethodSelect?: SelectPresentationProps;
    paymentProfileBillingAddress?: any;
    paymentProfileExpiredErrorWrapper?: InjectableCss;
    paymentProfileExpiredErrorText?: TypographyPresentationProps;
    paymentProfileEditCardLink?: LinkPresentationProps;
    mainContainer?: GridContainerProps;
    savedPaymentProfile?: SavedPaymentProfileEntryStyles;
    creditCardDetailsGridItem?: GridItemProps;
    creditCardDetails?: CreditCardStyles;
    creditCardAddressGridItem?: GridItemProps;
    creditCardAddress?: CreditCardBillingAddressEntryStyles;
}

export const checkoutReviewAndSubmitPaymentDetailsStyles: CheckoutReviewAndSubmitPaymentDetailsStyles = {
    fieldset: {
        css: css`
            margin: 0;
            padding: 0;
            border: 0;
        `,
    },
    paymentDetailsHeading: { variant: "h5" },
    paymentMethodAndPONumberContainer: {
        gap: 10,
        css: css`
            margin-bottom: 1rem;
        `,
    },
    paymentMethodGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            > * {
                margin-bottom: 8px;
                &:last-child {
                    margin-bottom: 0;
                }
            }

            ${MediaQuery.gte(680)} {
                max-width: 400px;
                width: 400px;
            }
        `,
    },
    paymentProfileExpiredErrorWrapper: {
        css: css`
            display: flex;
            width: 100%;
        `,
    },
    paymentProfileExpiredErrorText: { color: "danger" },
    paymentProfileEditCardLink: {
        css: css`
            margin-left: 1rem;
        `,
    },
    creditCardDetailsGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            ${MediaQuery.gte(680)} {
                max-width: 400px;
                width: 400px;
            }
        `,
    },
    creditCardDetails: {
        wrapper: {
            css: css`
                width: 100%;
            `,
        },
        cvvContainer: {
            css: css`
                margin-top: 20px;
                overflow: visible;
            `,
        },
    },
    creditCardAddressGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            flex-direction: column;
            z-index: 2;
            &&& {
                margin-top: 20px;
            }
        `,
    },
};

declare const TokenEx: TokenExType;
let tokenExIframe: IFrame | undefined;

let tokenExFrameStyleConfig: TokenExIframeStyles;

const isNonEmptyString = (value: string | undefined) => value !== undefined && value.trim() !== "";

const isMonthAndYearBeforeToday = (month: number, year: number) => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    return year < currentYear || (year === currentYear && month < currentMonth + 1);
};

const convertTokenExCardTypeToApiData = (cardType: string) => {
    if (cardType.includes("american")) {
        return "AMERICAN EXPRESS";
    }

    return cardType.toUpperCase();
};

const convertApiDataToTokenExCardType = (cardType: string) => {
    const loweredCardType = cardType.toLowerCase();

    if (loweredCardType === "mastercard") {
        return "masterCard";
    }
    if (loweredCardType === "american express") {
        return "americanExpress";
    }

    return loweredCardType;
};

const styles = checkoutReviewAndSubmitPaymentDetailsStyles;
const StyledForm = getStyledWrapper("form");
const StyledFieldSet = getStyledWrapper("fieldset");

const CreditCardIFrame = forwardRef<any, any>((props, ref) => {
    const {
        loadBillTo,
        cartState,
        billToState,
        tokenExConfigs,
        orderConfirmationPageLink,
        savedPaymentsPageLink,
        loadTokenExConfig,
        customPaymentMethods,
        achPaymentMethodName,
        makePaymentSubmit,
        loadCurrentCountries,
        isInvoicePaymentPage,
        allowedPaymentMethodsForInvoicePayment,
    }: Props = props;

    const theme = useContext(ThemeContext);
    const history = useHistory();

    const tokenExLibraryIsLoaded = useTokenExFrame();
    const spreedlyLibraryIsLoaded = useSpreedlyFrame();

    useImperativeHandle(ref, () => ({
        makeCreditCardPayment() {
            if (!validateForm()) {
                setShowFormErrors(true);
                return false;
            }

            if (paymentMethodDto?.isPaymentProfile || (paymentMethodDto?.isCreditCard && useTokenExGateway)) {
                creditCardDetails.current?.tokenize();
                savedPaymentProfile.current?.tokenize();
            } else {
                makePaymentSubmit(generateParameter());
            }
        },
    }));

    const generateParameter = () => {
        let creditCard = null;
        const selectedCountry = countries?.find(country => country.id === countryId) || null;
        const selectedState = selectedCountry?.states?.find(state => state.id === stateId) || null;
        creditCard = {
            cardType,
            cardHolderName,
            cardNumber,
            expirationMonth,
            expirationYear,
            securityCode,
            useBillingAddress,
            address1,
            city,
            state: selectedState?.name || null,
            stateAbbreviation: selectedState?.abbreviation || null,
            country: selectedCountry?.name || null,
            countryAbbreviation: selectedCountry?.abbreviation || null,
            postalCode,
        };
        return {
            PaymentMethod: paymentMethodDto,
            CreditCard: creditCard,
        };
    };

    const paymentDetailsFormState = usePaymentDetailsFormState();
    const {
        paymentMethod,
        setPaymentMethod,
        poNumber,
        vatNumber,
        saveCard,
        cardHolderName,
        setCardHolderName,
        cardNumber,
        setCardNumber,
        cardType,
        setCardType,
        expirationMonth,
        setExpirationMonth,
        expirationYear,
        setExpirationYear,
        securityCode,
        setSecurityCode,
        useBillingAddress,
        setUseBillingAddress,
        address1,
        setAddress1,
        countryId,
        setCountryId,
        stateId,
        setStateId,
        city,
        setCity,
        postalCode,
        setPostalCode,
        paymentMethodError,
        poNumberError,
        address1Error,
        countryError,
        stateError,
        cityError,
        postalCodeError,
        payPalError,
        paystandError,
        setPaystandError,
        showFormErrors,
        setShowFormErrors,
        isCardNumberTokenized,
        setIsCardNumberTokenized,
        isECheckTokenized,
        setIsECheckTokenized,
        accountHolderName,
        setAccountHolderName,
        accountNumber,
        setAccountNumber,
        routingNumber,
        setRoutingNumber,
        payerId,
        setPayerId,
        creditCardLastFourDigits,
        setCreditCardLastFourDigits,
        bankAccountLastFourDigits,
        setBankAccountLastFourDigits,
        isIframeLoaded,
        setIsIframeLoaded,
        isPayPal,
        setIsPayPal,
        runSubmitPayPal,
        setRunSubmitPayPal,
        paymentMethodRef,
        poNumberRef,
        address1Ref,
        countryRef,
        stateRef,
        cityRef,
        postalCodeRef,
        maskedCardNumber,
        setMaskedCardNumber,
    } = paymentDetailsFormState;
    const paymentDetailsCartState = usePaymentDetailsCartState(cartState, paymentMethod, countryId);
    const {
        cart,
        useTokenExGateway,
        useECheckTokenExGateway,
        usePaymetricGateway,
        useSquareGateway,
        useAdyenDropIn,
        usePaystandDropIn,
        countries,
        showPayPal,
        paymentOptions,
        paymentMethodDto,
        isCreditCard,
        isECheck,
        isPaymentProfile,
        selectedCountry,
        websiteSettings,
        useSpreedlyDropIn,
    } = paymentDetailsCartState;
    const paymentDetailsFormValidation = usePaymentDetailsFormValidation(
        paymentDetailsFormState,
        paymentDetailsCartState,
    );
    const {
        validatePaymentMethod,
        validatePONumber,
        validateAddress1,
        validateCountry,
        validateState,
        validateCity,
        validatePostalCode,
    } = paymentDetailsFormValidation;
    const {
        handlePaymentMethodChange,
        handlePONumberChange,
        handleVatNumberChange,
        handleSaveCardChange,
        handleCardHolderNameChange,
        handleCardNumberChange,
        handleCardTypeChange,
        handleExpirationMonthChange,
        handleExpirationYearChange,
        handleSecurityCodeChange,
        handleUseBillingAddressChange,
        handleAddressChange,
        handleCountryChange,
        handleStateChange,
        handleCityChange,
        handlePostalCodeChange,
        handleEditCardClick,
    } = usePaymentDetailsFormHandlers({
        paymentDetailsFormState,
        paymentDetailsFormValidation,
        paymentDetailsCartState,
        history,
        savedPaymentsPageLink,
        setPaymentMethodCallback: (
            event: React.ChangeEvent<HTMLSelectElement>,
            paymentMethods: PaymentMethodDto[] | null | undefined,
        ) => {
            setCheckoutPaymentMethod({
                paymentMethod: paymentMethods?.find(method => method.name === event.target.value),
            });
        },
    });

    useEffect(() => {
        if (!billToState.value && !billToState.isLoading && cartState.value && cartState.value.billToId) {
            loadBillTo({ billToId: cartState.value.billToId });
        }
    }, [billToState]);

    useEffect(() => {
        loadCurrentCountries();
    }, []);

    useEffect(() => {
        if (isCardNumberTokenized) {
            makePaymentSubmit(generateParameter());
        }
    }, [isCardNumberTokenized]);

    useEffect(() => {
        if (!cartState.isLoading && cartState.value && cartState.value.paymentMethod) {
            // setPaymentMethod(cartState.value.paymentMethod.name);
        }
    }, [cartState.isLoading]);

    const creditCardDetails = useRef<CreditCardDetailsValidation>(null);
    const savedPaymentProfile = useRef<SavedPaymentProfileValidation>(null);
    let paymentMethods = paymentOptions ? paymentOptions.paymentMethods : undefined;

    if (isInvoicePaymentPage && allowedPaymentMethodsForInvoicePayment) {
        paymentMethods = paymentMethods?.filter(p => {
            return (
                allowedPaymentMethodsForInvoicePayment.indexOf(p.name) > -1 ||
                (p.isPaymentProfile &&
                    !p.isPaymentProfileExpired &&
                    allowedPaymentMethodsForInvoicePayment.indexOf("Credit Card") > -1)
            );
        });
    }

    useEffect(() => {
        if (isInvoicePaymentPage && allowedPaymentMethodsForInvoicePayment) {
            paymentMethods = paymentMethods?.filter(p => {
                return (
                    allowedPaymentMethodsForInvoicePayment.indexOf(p.name) > -1 ||
                    (p.isPaymentProfile &&
                        !p.isPaymentProfileExpired &&
                        allowedPaymentMethodsForInvoicePayment.indexOf("Credit Card") > -1)
                );
            });
        }
    }, [paymentOptions]);

    if (!paymentMethod && paymentMethods?.length === 1) {
        setTimeout(() => {
            setPaymentMethod(paymentMethods![0].name);
        }, 300);
    }

    const tokenName = paymentMethodDto?.isPaymentProfile
        ? paymentMethodDto.name
        : useTokenExGateway && paymentMethodDto?.isCreditCard
        ? ""
        : undefined;

    useEffect(() => {
        if (typeof tokenName !== "undefined") {
            loadTokenExConfig({ token: tokenName });
        }
    }, [paymentMethod]);

    useEffect(() => {
        if (!useBillingAddress && countries?.length === 1) {
            setCountryId(countries[0].id);
        }
    }, [useBillingAddress]);

    const tokenExConfig = typeof tokenName !== "undefined" ? tokenExConfigs[tokenName] : undefined;

    const dispatch = useDispatch();
    const xHandlePaymentMethodChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        dispatch({
            type: "Data/InvoicePayment/ChangePaymentMethod",
            paymentMethod: paymentMethods?.find(item => item.name === event.currentTarget.value),
        });
        setPaymentMethod(event.currentTarget.value);
        validatePaymentMethod(event.currentTarget.value);
        handlePaymentMethodChange(event);
    };

    const validateForm = () => {
        const paymentMethodValid = validatePaymentMethod(paymentMethod);

        let creditCardValid = true;
        if (creditCardDetails?.current) {
            creditCardValid = creditCardDetails.current.validate();
        }
        if (savedPaymentProfile?.current) {
            creditCardValid = savedPaymentProfile?.current.validate();
        }

        const address1Valid = validateAddress1(address1);
        const countryValid = validateCountry(countryId);
        const stateValid = validateState(stateId);
        const cityValid = validateCity(city);
        const postalCodeValid = validatePostalCode(postalCode);

        return (
            paymentMethodValid &&
            creditCardValid &&
            address1Valid &&
            countryValid &&
            stateValid &&
            cityValid &&
            postalCodeValid
        );
    };

    if (!tokenExFrameStyleConfig) {
        tokenExFrameStyleConfig = generateTokenExFrameStyleConfig({ theme });
    }

    if (!cart || cart.requiresApproval || !paymentOptions || !orderConfirmationPageLink) {
        return null;
    }

    const iframeName = useTokenExGateway
        ? "TokenEx"
        : usePaymetricGateway
        ? "Paymetric"
        : useSquareGateway
        ? "Square"
        : usePaystandDropIn
        ? "Paystand"
        : useSpreedlyDropIn
        ? "Spreedly"
        : undefined;

    return (
        <StyledForm
            {...styles.form}
            id="reviewAndSubmitPaymentForm"
            onSubmit={undefined}
            noValidate={true}
            data-test-selector="reviewAndSubmitPaymentForm"
        >
            <StyledFieldSet {...styles.fieldset}>
                <GridContainer {...styles.paymentMethodAndPONumberContainer}>
                    {paymentMethods && paymentMethods.length > 0 && (
                        <GridItem {...styles.paymentMethodGridItem}>
                            <Select
                                {...styles.paymentMethodSelect}
                                label={translate("Payment Method")}
                                value={paymentMethod ?? paymentMethodDto?.name}
                                onChange={xHandlePaymentMethodChange}
                                required
                                error={showFormErrors && paymentMethodError}
                                data-test-selector="checkoutReviewAndSubmit_paymentMethod"
                                disabled={paymentMethods.length === 1}
                            >
                                <option value="">{translate("Select Payment Method")}</option>
                                {paymentMethods.map(method => (
                                    <option key={method.name} value={method.name}>
                                        {method.description}
                                    </option>
                                ))}
                            </Select>

                            {paymentMethod && paymentMethod === achPaymentMethodName && <Ach />}

                            {paymentMethodDto?.isPaymentProfile && !paymentMethodDto.isPaymentProfileExpired && (
                                <PaymentProfileBillingAddress
                                    address={paymentMethodDto.billingAddress}
                                    extendedStyles={styles.paymentProfileBillingAddress}
                                />
                            )}
                            {paymentMethodDto?.isPaymentProfile && paymentMethodDto.isPaymentProfileExpired && (
                                <StyledWrapper {...styles.paymentProfileExpiredErrorWrapper}>
                                    <Typography {...styles.paymentProfileExpiredErrorText}>
                                        {siteMessage("Checkout_PaymentProfileExpired")}
                                    </Typography>
                                    {savedPaymentsPageLink && (
                                        <HistoryContext.Consumer>
                                            {({ history }) => {
                                                return (
                                                    <Link
                                                        {...styles.paymentProfileEditCardLink}
                                                        onClick={() => handleEditCardClick()}
                                                    >
                                                        {translate("Edit Card")}
                                                    </Link>
                                                );
                                            }}
                                        </HistoryContext.Consumer>
                                    )}
                                </StyledWrapper>
                            )}
                        </GridItem>
                    )}
                    {paymentMethodDto?.isPaymentProfile && !paymentMethodDto.isPaymentProfileExpired && (
                        <GridItem {...styles.creditCardDetailsGridItem}>
                            <SavedPaymentProfileEntry
                                iframe={iframeName}
                                onSecurityCodeUpdate={handleSecurityCodeChange}
                                extendedStyles={styles.savedPaymentProfile}
                                tokenExConfig={tokenExConfig}
                                tokenExFrameStyleConfig={tokenExFrameStyleConfig}
                                paymentMethodDto={paymentMethodDto}
                                updateCardIsTokenized={setIsCardNumberTokenized}
                                ref={savedPaymentProfile}
                            />
                        </GridItem>
                    )}
                    {cart.showCreditCard && paymentMethodDto?.isCreditCard && (
                        <>
                            <GridItem {...styles.creditCardDetailsGridItem}>
                                <CreditCardDetailsEntry
                                    canSaveCard={false}
                                    onSaveCardUpdate={handleSaveCardChange}
                                    onCardHolderNameUpdate={handleCardHolderNameChange}
                                    onCardNumberUpdate={handleCardNumberChange}
                                    onCardTypeUpdate={handleCardTypeChange}
                                    onExpirationMonthUpdate={handleExpirationMonthChange}
                                    onExpirationYearUpdate={handleExpirationYearChange}
                                    onSecurityCodeUpdate={handleSecurityCodeChange}
                                    availableCardTypes={paymentOptions.cardTypes ?? []}
                                    availableMonths={paymentOptions.expirationMonths ?? []}
                                    availableYears={paymentOptions.expirationYears ?? []}
                                    isIframeLoaded={isIframeLoaded}
                                    error={showFormErrors ? paystandError : undefined}
                                    bankAccountLastFourDigits={bankAccountLastFourDigits}
                                    creditCardLastFourDigits={creditCardLastFourDigits}
                                    tokenExConfig={tokenExConfig}
                                    tokenExFrameStyleConfig={tokenExFrameStyleConfig}
                                    updateCardIsTokenized={setIsCardNumberTokenized}
                                    updateMaskedCardNumber={setMaskedCardNumber}
                                    ref={creditCardDetails}
                                    iframe={iframeName}
                                />
                            </GridItem>
                            <GridItem {...styles.creditCardAddressGridItem}>
                                <CreditCardBillingAddressEntry
                                    useBillTo={useBillingAddress}
                                    onUseBillToChange={handleUseBillingAddressChange}
                                    billTo={billToState.value}
                                    address1={address1}
                                    onAddress1Change={handleAddressChange}
                                    address1Error={showFormErrors ? address1Error : undefined}
                                    country={countryId}
                                    onCountryChange={handleCountryChange}
                                    countryError={showFormErrors ? countryError : undefined}
                                    state={stateId}
                                    onStateChange={handleStateChange}
                                    stateError={showFormErrors ? stateError : undefined}
                                    city={city}
                                    onCityChange={handleCityChange}
                                    cityError={showFormErrors ? cityError : undefined}
                                    postalCode={postalCode}
                                    onPostalCodeChange={handlePostalCodeChange}
                                    postalCodeError={showFormErrors ? postalCodeError : undefined}
                                    availableCountries={countries ?? []}
                                    availableStates={selectedCountry?.states}
                                    extendedStyles={styles.creditCardAddress}
                                />
                            </GridItem>
                        </>
                    )}
                </GridContainer>
            </StyledFieldSet>
        </StyledForm>
    );
});

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(CreditCardIFrame);
